'use client'

import {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
} from 'react'

export type SupportedLocale = 'en' | 'de'

export const SUPPORTED_LANGUAGES: { code: SupportedLocale; label: string; flag: string }[] = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'de', label: 'German', flag: '🇩🇪' },
]

export function LanguageFlagIcon({
    code,
    className = '!h-5 !w-5',
}: {
    code: SupportedLocale
    className?: string
}) {
    if (code === 'en') {
        return (
            <svg
                viewBox="0 0 32 32"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                className={className}
            >
                <path d="m0 5.503h32v20.993h-32z" fill="#fff"></path>
                <path d="m14.649 5.503v8.838h-14.649v3.742h14.649v8.414h2.967v-8.414h14.384v-3.742h-14.384v-8.838z" fill="#bd0034"></path>
                <path d="m13.659 5.503h-11.159l11.159 7.057z" fill="#1a237b"></path>
                <path d="m0 13.094h7.865l-7.865-4.987z" fill="#1a237b"></path>
                <path d="m0 5.5v1.228l9.439 5.893h2.318s-9.303-5.762-11.513-7.121z" fill="#bd0034"></path>
                <path d="m13.659 26.497h-11.159l11.159-7.057z" fill="#1a237b"></path>
                <path d="m0 18.906h7.865l-7.865 4.987z" fill="#1a237b"></path>
                <path d="m0 26.5v-1.228l9.439-5.893h2.318s-9.303 5.762-11.513 7.121z" fill="#bd0034"></path>
                <path d="m18.341 5.503h11.159l-11.159 7.057z" fill="#1a237b"></path>
                <path d="m32 13.094h-7.865l7.865-4.987z" fill="#1a237b"></path>
                <path d="m32 5.5v1.228l-9.439 5.893h-2.318s9.3-5.762 11.513-7.118z" fill="#bd0034"></path>
                <path d="m18.341 26.497h11.159l-11.159-7.057z" fill="#1a237b"></path>
                <path d="m32 18.906h-7.865l7.865 4.987z" fill="#1a237b"></path>
                <path d="m32 26.5v-1.228l-9.439-5.893h-2.318s9.3 5.762 11.513 7.118z" fill="#bd0034"></path>
            </svg>
        )
    }

    return (
        <svg
            viewBox="0 0 365.773 365.773"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className={className}
        >
            <g>
                <g>
                    <path d="m0 82.944h365.773v66.56h-365.773z" fill="#383a45"></path>
                    <path d="m0 149.504h365.773v66.56h-365.773z" fill="#c2325d"></path>
                    <path d="m0 216.269h365.773v66.56h-365.773z" fill="#ff9551"></path>
                </g>
            </g>
        </svg>
    )
}

const STORAGE_KEY = 'airies_lang'
export const LOCALE_COOKIE_NAME = 'airies_lang'
const DEFAULT_LOCALE: SupportedLocale = 'en'

type LanguageContextValue = {
    locale: SupportedLocale
    setLocale: (locale: SupportedLocale) => void
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined)

function getStoredLocale(): SupportedLocale {
    if (typeof window === 'undefined') return DEFAULT_LOCALE
    
    // Try localStorage first
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'en' || stored === 'de') return stored
    
    // Fallback to cookie
    const cookieMatch = document.cookie.match(new RegExp(`(^| )${LOCALE_COOKIE_NAME}=([^;]+)`))
    const cookieValue = cookieMatch?.[2]
    if (cookieValue === 'en' || cookieValue === 'de') return cookieValue
    
    return DEFAULT_LOCALE
}

function setCookie(name: string, value: string, days: number = 365) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString()
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`
}

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [locale, setLocaleState] = useState<SupportedLocale>(DEFAULT_LOCALE)

    // On mount, read from localStorage/cookie and sync cookie.
    // We intentionally keep rendering children while this runs so the app
    // never shows a blank screen on first load.
    useEffect(() => {
        const storedLocale = getStoredLocale()
        setLocaleState(storedLocale)
        
        // Ensure cookie is in sync with localStorage
        setCookie(LOCALE_COOKIE_NAME, storedLocale)
    }, [])

    const setLocale = (newLocale: SupportedLocale) => {
        if (newLocale === locale) return

        // Persist to localStorage
        localStorage.setItem(STORAGE_KEY, newLocale)
        
        // Also persist to cookie (for Server Components)
        setCookie(LOCALE_COOKIE_NAME, newLocale)

        // Reload the page to fetch data in the new language
        window.location.reload()
    }

    return (
        <LanguageContext.Provider value={{ locale, setLocale }}>
            {children}
        </LanguageContext.Provider>
    )
}

export function useLanguage(): LanguageContextValue {
    const context = useContext(LanguageContext)
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider')
    }
    return context
}
